import { FiGrid } from 'react-icons/fi'
import { Link } from 'react-router-dom'

import { t } from 'i18next'

import BudgetPacingProgressBar from '~/components/BudgetPacingProgressBar'
import ProgressBar from '~/components/ProgressBar'
import Tooltip from '~/components/Tooltip'
import { canShowSpendReportLink } from '~/modules/retailMedia/helpers/canShowSpendReportLink'
import type { PacingStatus } from '~/modules/retailMedia/services/campaign/spend-reports'

interface BudgetCellProps {
  budget: {
    type: string
    totalDaily: string
    spent: string
    raw: {
      dailyBudget: number
      dailyConsumed: number
    }
  }
  raw: {
    allow_budget_pacing?: boolean
    budget_pacing_active?: boolean
    daily_pacing?: PacingStatus
    [key: string]: unknown
  }
  id: string
}

const AmountRow = ({
  totalDaily,
  budgetType
}: {
  totalDaily: string
  budgetType: string
}) => (
  <div className="d-inline-flex align-items-center lh-1">
    <span className="fs-6">{totalDaily}</span>
    {budgetType === 'evenly' && (
      <Tooltip
        icon={<FiGrid size={10} />}
        text={t('rm:settings.budget_type.values.evenly.description')}
      />
    )}
  </div>
)

const BudgetCell = ({ budget, raw, id }: BudgetCellProps) => {
  if (!budget.totalDaily) return null

  const pacingEnabled = !!raw?.allow_budget_pacing
  const canLink = canShowSpendReportLink(
    raw?.allow_budget_pacing,
    raw?.budget_pacing_active
  )

  const progressBar = pacingEnabled ? (
    <BudgetPacingProgressBar
      style={{ width: '7rem' }}
      goal={budget.raw.dailyBudget}
      value={budget.raw.dailyConsumed}
      pacing={raw?.daily_pacing ?? 'on_track'}
    >
      <AmountRow totalDaily={budget.totalDaily} budgetType={budget.type} />
    </BudgetPacingProgressBar>
  ) : (
    <>
      <AmountRow totalDaily={budget.totalDaily} budgetType={budget.type} />
      <ProgressBar
        style={{ width: '7rem' }}
        goal={budget.raw.dailyBudget}
        value={budget.raw.dailyConsumed}
      />
    </>
  )

  const inner = <div className="d-flex flex-column gap-1">{progressBar}</div>

  if (!pacingEnabled) {
    return (
      <Tooltip
        noMargin
        icon={inner}
        text={`${budget.spent} — ${budget.totalDaily}`}
      />
    )
  }

  if (!canLink) {
    return (
      <Tooltip
        noMargin
        icon={inner}
        text={t('rm:campaignTable.budget.reportUnavailable')}
      />
    )
  }

  return (
    <Tooltip
      noMargin
      cursor="pointer"
      icon={
        <Link
          to={`/retail-media/campaign/view/${id}/spend-reports`}
          replace
          style={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
            display: 'block'
          }}
        >
          {inner}
        </Link>
      }
      text={t('rm:campaignTable.budget.viewReport')}
    />
  )
}

export default BudgetCell
